import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddButton from 'components/v2/buttons/AddButton';
import List from 'components/v2/list/List';
import {
  deleteFscSettings,
  fscPeriodType,
  getFscSettings,
} from 'utils/FscSettingsApiUtils';
import { showToast } from 'stores/ToastStore';
import Modal from 'components/v2/modal';
import Button from 'components/v2/buttons/Button';
import FSCSettingListItem from './FSCSettingListItem';
import AddFSCSettings from './AddFSCSetting';
import FSCNotApplied from './FSCNotApplied';
import Panel from 'components/v2/Panel';
import Alert from 'components/ui/Alert';

export default function Active({ accountId, onChange, actionsEnabled }) {
  const { t } = useTranslation();
  const [showAddFSCSettingsForm, setShowAddFSCSettingsForm] = useState(false);
  const [currentSettings, setCurrentSettings] = useState();
  const [upcomingSettings, setUpcomingSettings] = useState([]);
  const [showConfirmDeleteSettingModal, setShowConfirmDeleteSettingModal] =
    useState(false);
  const [selectedSettings, setSelectedSetting] = useState({
    id: null,
  });
  const [loadingCurrent, setLoadingCurrent] = useState(false);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  async function loadCurrentSettings() {
    try {
      setLoadingCurrent(true);

      const { rows, count } = await getFscSettings(
        accountId,
        fscPeriodType.CURRENT,
      );

      setCurrentSettings(count > 0 ? rows[0] : null);
    } catch (e) {
      console.error(e);
      showToast(t('ERROR'), t('ERROR_WHILE_RETRIEVING_SETTINGS'), 'error');
    } finally {
      setLoadingCurrent(false);
    }
  }

  async function loadUpcomingSettings() {
    try {
      setLoadingUpcoming(true);

      const { rows } = await getFscSettings(accountId, fscPeriodType.UPCOMING, {
        perPage: 1000,
        orderType: 'ASC',
      });

      setUpcomingSettings(rows);
    } catch (e) {
      console.error(e);
      showToast(t('ERROR'), t('ERROR_WHILE_RETRIEVING_SETTINGS'), 'error');
    } finally {
      setLoadingUpcoming(false);
    }
  }

  function loadAllSettings() {
    loadCurrentSettings();
    loadUpcomingSettings();
  }

  useEffect(() => {
    loadAllSettings();
  }, []);

  function handleShowConfirmDeleteSettingModal(setting) {
    setSelectedSetting(setting);
    setShowConfirmDeleteSettingModal(true);
  }

  function handleCloseConfirmDeleteSettingModal() {
    setShowConfirmDeleteSettingModal(false);
    setSelectedSetting(null);
  }

  function handleSave() {
    loadAllSettings();
    onChange();
  }

  async function handleDelete() {
    const id = selectedSettings.id;

    handleCloseConfirmDeleteSettingModal();

    try {
      await deleteFscSettings(id);
      loadAllSettings();
      showToast(t('DELETED'), t('FSC_SETTING_DELETED_SUCCESSFULLY'));
    } catch (e) {
      console.error(e);
      showToast(t('ERROR'), t('ERROR_WHILE_DELETING_SETTING'), 'error');
    }
  }

  function renderAddFSCSettingsForm() {
    if (!showAddFSCSettingsForm) {
      return null;
    }

    return (
      <div>
        <AddFSCSettings
          accountId={accountId}
          onCancel={() => setShowAddFSCSettingsForm(false)}
          onSave={() => {
            setShowAddFSCSettingsForm(false);
            handleSave();
          }}
        />
        <hr className="my-8" />
      </div>
    );
  }

  function renderHeading() {
    return (
      <div className="mb-4 flex items-center space-x-3">
        <h3>{t('DRAYAGE_FSC_SETTINGS')}</h3>
        {actionsEnabled && (
          <AddButton
            size="small"
            onClick={() => setShowAddFSCSettingsForm(true)}
            dataTestId="drayage-fsc-settings-add-button"
          />
        )}
      </div>
    );
  }

  function renderCurrentSettings() {
    const renderedCurrentSettings = currentSettings ? (
      <FSCSettingListItem
        defaultOpen
        data={currentSettings}
        onChange={() => handleSave()}
        onDelete={handleShowConfirmDeleteSettingModal}
        actionsEnabled={actionsEnabled}
        testDataId="fsc-settings-item"
      />
    ) : (
      !loadingCurrent && <FSCNotApplied />
    );

    return loadingCurrent ? (
      <div>{t('LOADING')}</div>
    ) : (
      <List
        title={t('CURRENT')}
        className="mb-4"
        testDataId="current-section-label"
      >
        {renderedCurrentSettings}
      </List>
    );
  }

  function renderUpcoming() {
    return loadingUpcoming ? (
      <div>{t('LOADING')}</div>
    ) : (
      <>
        <hr className="my-8" />
        <List title={t('UPCOMING')} testDataId="upcoming-section-label">
          {upcomingSettings.length > 0 ? (
            upcomingSettings.map((data, i) => (
              <FSCSettingListItem
                data={data}
                key={i}
                onChange={() => handleSave()}
                onDelete={handleShowConfirmDeleteSettingModal}
                actionsEnabled={actionsEnabled}
              />
            ))
          ) : (
            <Panel>
              <Alert type="warning">
                {t('NO_UPCOMING_FSC_CONFIGURATION')}
              </Alert>
            </Panel>
          )}
        </List>
      </>
    );
  }

  function renderModalFooter() {
    return (
      <div className="flex justify-end gap-2">
        <Button color="green" onClick={handleDelete}>
          {t('DELETE')}
        </Button>
        <Button onClick={handleCloseConfirmDeleteSettingModal}>
          {t('CANCEL')}
        </Button>
      </div>
    );
  }

  return (
    <div className="lx:w-1/2 w-full">
      {renderHeading()}
      {renderAddFSCSettingsForm()}
      {renderCurrentSettings()}
      {renderUpcoming()}

      <Modal
        show={showConfirmDeleteSettingModal}
        onHide={handleCloseConfirmDeleteSettingModal}
        onActionButtonClick={handleDelete}
        title={t('DELETE_FSC_SETTING')}
        footer={renderModalFooter()}
        showCloseButton
      >
        {t('CONFIRM_DELETE_FSC_SETTING')}
      </Modal>
    </div>
  );
}
